/**
 * Zcash Helpers - Utilities and extensions for Zcash functionality
 * 
 * Migrated to zcash-extensions wrapper architecture.
 * This file contains utilities for working with Zcash addresses, networks, and transactions.
 */

import { Chain, ChainToChainId, ChainToRPC, DerivationPath, RPCUrl, type UTXOChain, ExplorerUrl } from "@doritokit/helpers";
import { Network as bitcoinjsNetwork, payments, crypto, script as bitcoinScript, address as bitcoinAddress } from "bitcoinjs-lib";
import bs58check from 'bs58check';
// TODO: Copy zcashTransactionParser to utils/ and update import
// import { isValidZcashTransaction, createBitcoinCompatibleTx } from './zcashTransactionParser';

// For now, placeholder functions until we copy the transaction parser
const isValidZcashTransaction = (buffer: Buffer): boolean => {
	// Placeholder - check if buffer contains Zcash-specific version bytes
	if (buffer.length < 8) return false;
	const version = buffer.readUInt32LE(0);
	return (version & 0x80000000) !== 0; // Zcash transactions have version with sign bit set
};

const createBitcoinCompatibleTx = (zcashTxBuffer: Buffer): Buffer => {
	// Placeholder - return as-is for now
	return zcashTxBuffer;
};

// Extend Chain enum with Zcash
export const ExtendedChain = {
	...Chain,
	Zcash: "ZEC" as UTXOChain
};

// Extend RPCUrl enum with Zcash
export const ExtendedRPCUrl = {
	...RPCUrl,
	Zcash: "https://rpc.zcash.winbit32.com"
};

export const ExtendedExplorerUrl = {
	...ExplorerUrl,
	Zcash: "https://explorer.zcha.in"
};

// Extend ChainToRPC with Zcash
export const ExtendedChainToRPC = {
	...ChainToRPC,
	[ExtendedChain.Zcash]: ExtendedRPCUrl.Zcash
};

// Extend DerivationPath with Zcash and missing chains
export const ExtendedDerivationPath = {
	...DerivationPath,
	[Chain.Dash]: "m/44'/5'/0'/0",
	// EVM chains all use Ethereum derivation path (coin type 60)
	[Chain.Arbitrum]: "m/44'/60'/0'/0", // Arbitrum
	[Chain.Optimism]: "m/44'/60'/0'/0", // Optimism
	[Chain.Polygon]: "m/44'/60'/0'/0", // Polygon
	[Chain.Base]: "m/44'/60'/0'/0", // Base
	[ExtendedChain.Zcash]: "m/44'/133'/0'/0/0", // Zcash default transparent address index 0
	ZcashShielded: "m/32'/133'/0'", // ZIP-32 shielded derivation path
	// Add string literal fallbacks for compatibility with various naming conventions
	ARB: "m/44'/60'/0'/0", // Arbitrum fallback
	ARBITRUM: "m/44'/60'/0'/0", // Alternative name for Arbitrum
	OP: "m/44'/60'/0'/0", // Optimism fallback
	OPTIMISM: "m/44'/60'/0'/0", // Alternative name for Optimism
	MATIC: "m/44'/60'/0'/0", // Polygon fallback
	POLYGON: "m/44'/60'/0'/0", // Alternative name for Polygon
	BASE: "m/44'/60'/0'/0", // Base fallback
};

// Extend ChainToChainId with Zcash
export const ExtendedChainToChainId = {
	...ChainToChainId,
	[ExtendedChain.Zcash]: "ZEC"
};

// Zcash address prefixes
export const ZcashPrefixes = {
	// Transparent addresses
	tMainnet: Buffer.from([0x1C, 0xB8]),
	tTestnet: Buffer.from([0x1D, 0x25]),
	
	// Shielded addresses (Sapling)
	zSMainnet: Buffer.from([0x16, 0x9A]),
	zSTestnet: Buffer.from([0x16, 0xB6]),
};

// Create a network configuration for Zcash to use with bitcoinjs-lib
// The actual Zcash pubKeyHash is 0x1cb8 and scriptHash is 0x1cbd, which are outside UInt8 range
// We'll use values within UInt8 range and handle the prefix separately
export const zcashNetwork: bitcoinjsNetwork = {
	messagePrefix: '\x18Zcash Signed Message:\n',
	bech32: 'zs',
	bip32: {
		public: 0x0488b21e,
		private: 0x0488ade4
	},
	pubKeyHash: 0x00, // We'll handle the actual 0x1cb8 prefix in custom address functions
	scriptHash: 0x00, // We'll handle the actual 0x1cbd prefix in custom address functions
	wif: 0x80
};

// Check if an address is a valid Zcash address (transparent, shielded, or unified)
export const isValidZcashAddress = (address: string): { isValid: boolean; type: 'transparent' | 'shielded' | 'unified' | 'invalid' } => {
	try {
		// Check if it's a unified address starting with 'u1'
		if (address.startsWith('u1')) {
			// Basic validation for unified address format
			// Updated range to support full unified addresses with 3 receivers (T+S+O = ~200+ chars)
			// Based on ZIP-316: T+S = ~116 chars, T+S+O = ~213 chars, range should be 50-300
			if (address.length >= 50 && address.length <= 300) {
				console.log("Valid unified address detected");
				return { isValid: true, type: 'unified' };
			}
			console.log(`Unified address length ${address.length} is outside valid range (50-300)`);
			return { isValid: false, type: 'invalid' };
		}
		
		// Check if it's a transparent address
		if (address.startsWith('t')) {
			const decoded = bs58check.decode(address);
			const prefix = decoded.length >= 2 ? decoded.slice(0, 2) : Buffer.alloc(2);
			
			if (Buffer.compare(prefix, ZcashPrefixes.tMainnet) === 0 || 
				Buffer.compare(prefix, ZcashPrefixes.tTestnet) === 0) {
				return { isValid: true, type: 'transparent' };
			}
		}
		
		// Check if it's a shielded address starting with 'z'
		if (address.startsWith('z')) {
			const decoded = bs58check.decode(address);
			const prefix = decoded.length >= 2 ? decoded.slice(0, 2) : Buffer.alloc(2);
			
			if (Buffer.compare(prefix, ZcashPrefixes.zSMainnet) === 0 || 
				Buffer.compare(prefix, ZcashPrefixes.zSTestnet) === 0) {
				console.log("Valid 'z' shielded address detected");
				return { isValid: true, type: 'shielded' };
			}
		}
		
		return { isValid: false, type: 'invalid' };
	} catch (error) {
		console.error("Error validating Zcash address:", error);
		return { isValid: false, type: 'invalid' };
	}
};

/**
 * Cheap shape check for a Unified Full Viewing Key (UFVK). A real
 * mainnet UFVK is Bech32m-encoded with a `uview1` HRP and is far
 * longer than a regular address; we only gate UI affordances on this
 * (the WASM `UnifiedFullViewingKey` constructor does the authoritative
 * validation when the key is actually used). Shared by the NFPT
 * viewer, the Zcash send-core hook and the View & Receive wizard so
 * the "looks like a viewing key" rule lives in exactly one place.
 */
export const looksLikeUfvk = (value: string): boolean =>
	/^uview1[0-9a-z]{50,}$/.test(String(value || '').trim().toLowerCase());

// Convert a Zcash address to an output script
export const toZcashOutputScript = (address: string): Buffer => {
	try {
		// First validate that it's a Zcash address
		const validationResult = isValidZcashAddress(address);
		if (!validationResult.isValid) {
			throw new Error(`Invalid Zcash address: ${address}`);
		}
		
		// Handle transparent addresses (starting with 't')
		if (validationResult.type === 'transparent') {
			// Decode the address
			const decoded = bs58check.decode(address);
			
			// Extract the hash160 part (skipping the 2-byte prefix)
			const pubKeyHash = decoded.length > 2 ? decoded.slice(2) : Buffer.alloc(20); // Ensure we have valid hash160
			
			// Log the pubKeyHash for debugging
			console.log(`Creating P2PKH script for transparent address with pubKeyHash length: ${pubKeyHash.length}`);
			
			// Create a P2PKH output script
			// OP_DUP OP_HASH160 <pubKeyHash> OP_EQUALVERIFY OP_CHECKSIG
			return bitcoinScript.compile([
				bitcoinScript.OPS.OP_DUP,
				bitcoinScript.OPS.OP_HASH160,
				Buffer.from(pubKeyHash),
				bitcoinScript.OPS.OP_EQUALVERIFY,
				bitcoinScript.OPS.OP_CHECKSIG
			]);
		}
		
		// Handle shielded addresses (starting with 'z')
		if (validationResult.type === 'shielded') {
			// For 'z' addresses, we need to use the Zcash RPC to create transactions
			// For now, we'll return a placeholder script that will be recognized
			// But actual transaction creation for 'z' addresses will be handled by
			// the RPC API using z_sendmany
			console.log("Creating placeholder output script for 'z' address. Actual transaction will use z_sendmany");
			
			// Create a null output script (this is just a placeholder)
			// Real z-address transactions must use the z_* RPC calls
			return Buffer.from([0x00]); // OP_0
		}
		
		// Handle unified addresses (starting with 'u1')
		if (validationResult.type === 'unified') {
			// For unified addresses, we need to use the Zcash RPC to create transactions
			// For now, we'll return a placeholder script that will be recognized
			// But actual transaction creation for unified addresses will be handled by
			// the RPC API using z_sendmany
			console.log("Creating placeholder output script for unified address. Actual transaction will use z_sendmany");
			
			// Create a null output script (this is just a placeholder)
			// Real unified address transactions must use the z_* RPC calls
			return Buffer.from([0x00]); // OP_0
		}
		
		throw new Error(`Unsupported Zcash address type: ${validationResult.type}`);
	} catch (error) {
		console.error("Error creating output script for Zcash address:", error);
		throw new Error(`Failed to create output script for address ${address}: ${error.message}`);
	}
};

// Patch the bitcoinjs-lib address module to handle Zcash addresses
export const patchBitcoinJsLibForZcash = () => {
	// Check if the module has already been patched to avoid double-patching
	if ((bitcoinAddress as any).__zcashPatched) {
		console.log("Bitcoin.js library already patched for Zcash addresses");
		return;
	}

	// Store the original toOutputScript function
	const originalToOutputScript = bitcoinAddress.toOutputScript;
	
	// Override the toOutputScript function to handle Zcash addresses
	bitcoinAddress.toOutputScript = (address: string, network: any) => {
		try {
			// First try to see if it's a Zcash address
			if (address && typeof address === 'string' && (address.startsWith('t') || address.startsWith('z') || address.startsWith('u1'))) {
				try {
					const validationResult = isValidZcashAddress(address);
					if (validationResult.isValid) {
						// It's a valid Zcash address, use our custom function
						console.log(`Using custom Zcash handler for address: ${address}`);
						return toZcashOutputScript(address);
					}
				} catch (e) {
					// If our custom validation failed, fall back to the original function
					console.log(`Zcash address validation failed for ${address}, falling back to original function:`, e);
				}
			}
			
			// If it's not a Zcash address or our handling failed, use the original function
			return originalToOutputScript(address, network);
		} catch (error) {
			// If the original function fails, check if it's a Zcash address and try our custom function
			if (address && typeof address === 'string' && (address.startsWith('t') || address.startsWith('z') || address.startsWith('u1'))) {
				try {
					console.log(`Original handler failed for ${address}, trying Zcash handler as fallback`);
					return toZcashOutputScript(address);
				} catch (zcashError) {
					// If our custom function also fails, throw the original error
					console.error(`Failed to handle address ${address} with custom Zcash handler:`, zcashError);
					throw error;
				}
			}
			
			// Otherwise, just throw the original error
			throw error;
		}
	};
	
	// Mark the module as patched to avoid double-patching
	(bitcoinAddress as any).__zcashPatched = true;
	
	console.log("Bitcoin.js library successfully patched to handle Zcash addresses");
};

// Apply the patch immediately when this module is imported
patchBitcoinJsLibForZcash();

// Custom function to create Zcash p2pkh address (transparent address)
export const createZcashAddress = (publicKey: Buffer): string => {
	// The Zcash mainnet t-address prefix is 0x1CB8
	const zcashPrefix = ZcashPrefixes.tMainnet;
	
	// Generate hash160 of the public key
	const pubKeyHash = crypto.hash160(publicKey);
	
	// Prepend Zcash prefix to form the proper address
	const addressBytes = Buffer.concat([zcashPrefix, pubKeyHash]);
	
	// Convert to base58check encoding
	return bs58check.encode(addressBytes);
};

// A function to help create transparent addresses with the proper 0x1c prefix
export const getZcashAddressPrefix = () => ZcashPrefixes.tMainnet;

// Function to generate a Zcash shielded address from a private key
// NOTE: Shielded addresses cannot be reliably generated locally
// They require proper cryptographic key derivation that is best handled by the Zcash daemon
// This function is deprecated - use RPC calls instead
export const createZcashShieldedAddress = (privateKey: Buffer): string => {
	console.warn("createZcashShieldedAddress is deprecated and unreliable");
	console.warn("Shielded addresses should be generated using RPC calls (z_getnewaccount + z_getaddressforaccount)");
	
	// Return empty string to indicate this method should not be used
	return "";
};

/**
 * Simple function to prepare a PSBT for Zcash signing - treat transparent addresses like Bitcoin
 * @param psbt Partially Signed Bitcoin Transaction object
 * @param inputs Transaction inputs
 * @returns Prepared PSBT ready for signing
 */
export const prepareZcashPsbtForSigning = (psbt: any, inputs: any[]) => {
	console.log(`Preparing PSBT for Zcash signing with ${inputs?.length || 0} inputs and ${psbt.data.inputs?.length || 0} PSBT inputs`);
	
	// For transparent Zcash addresses, treat exactly like Bitcoin - no special handling needed
	// The transaction format is identical, only the address prefixes are different
	
	return psbt;
};

/**
 * Asynchronously convert witnessUtxo to nonWitnessUtxo for Zcash transactions
 * This is needed because Zcash uses legacy-style addresses and transactions
 * @param input The PSBT input to convert
 * @param apiClient Client API to fetch raw transaction data
 * @returns Promise that resolves when the conversion is complete
 */
export const convertWitnessUtxoToNonWitnessUtxo = async (
	input: any, 
	apiClient: any
): Promise<void> => {
	// If input already has nonWitnessUtxo or doesn't have witnessUtxo, nothing to do
	if (input.nonWitnessUtxo || !input.witnessUtxo) {
		return;
	}
	
	// If we already have txHex, use it directly
	if (input.txHex) {
		input.nonWitnessUtxo = Buffer.from(input.txHex, 'hex');
		delete input.witnessUtxo;
		return;
	}
	
	// If we have txid but no txHex, try to fetch the raw transaction
	if (input.txid) {
		try {
			const txid = input.txid.toString('hex');
			console.log(`Fetching raw transaction data for ${txid}`);
			
			const txHex = await apiClient.getRawTx(txid);
			if (txHex) {
				input.nonWitnessUtxo = Buffer.from(txHex, 'hex');
				delete input.witnessUtxo;
			}
		} catch (fetchError) {
			console.error(`Failed to fetch transaction data: ${fetchError.message}`);
			// If we can't fetch the raw transaction, we'll continue with witnessUtxo
			// but this might cause signing issues with Zcash
		}
	}
};

/**
 * Process all inputs in a Zcash PSBT to ensure they are properly prepared for signing
 * @param psbt The PSBT to process
 * @param apiClient Client API to fetch raw transaction data
 * @returns Promise that resolves when all inputs are processed
 */
export const processZcashPsbtInputs = async (
	psbt: any,
	apiClient: any
): Promise<void> => {
	// For Zcash, we avoid using nonWitnessUtxo to prevent parsing issues
	if (!psbt.data.inputs || psbt.data.inputs.length === 0) {
		return;
	}
	
	// Process each input sequentially
	for (let i = 0; i < psbt.data.inputs.length; i++) {
		const input = psbt.data.inputs[i];
		
		// Special handling for Zcash inputs
		if (input._zcashTxHex) {
			console.log(`Input ${i} has Zcash transaction hex, storing for reference`);
			
			try {
				// Convert the stored hex to buffer for reference
				const zcashTxBuffer = Buffer.from(input._zcashTxHex, 'hex');
				
				// Validate it's a Zcash transaction
				if (isValidZcashTransaction(zcashTxBuffer)) {
					// Store the original Zcash transaction buffer for reference
					input._zcashTxBuffer = zcashTxBuffer;
					
					// Mark this input as Zcash for special handling during signing
					input._isZcashInput = true;
					
					// Remove nonWitnessUtxo if present to avoid parsing issues
					if (input.nonWitnessUtxo) {
						console.log(`Removing nonWitnessUtxo for Zcash input ${i} to prevent parsing errors`);
						delete input.nonWitnessUtxo;
					}
					
					console.log(`Successfully prepared Zcash input ${i} for witnessUtxo signing`);
				} else {
					console.warn(`Input ${i} has invalid Zcash transaction hex`);
				}
			} catch (error) {
				console.error(`Error processing Zcash transaction hex for input ${i}:`, error);
			}
		}
		// Remove any existing nonWitnessUtxo for Zcash transactions to prevent parsing issues
		else if (input.nonWitnessUtxo && isValidZcashTransaction(input.nonWitnessUtxo)) {
			console.log(`Input ${i} contains a Zcash transaction, removing nonWitnessUtxo to prevent parsing issues`);
			
			// Store the original Zcash transaction buffer for reference
			input._zcashTxBuffer = input.nonWitnessUtxo;
			
			// Remove the nonWitnessUtxo to avoid parsing issues
			delete input.nonWitnessUtxo;
			
			// Mark this input as Zcash for special handling during signing
			input._isZcashInput = true;
		}
		// For non-Zcash inputs, keep standard processing
		else if (input.witnessUtxo && !input.nonWitnessUtxo && !input._isZcashInput) {
			// Only convert to nonWitnessUtxo for non-Zcash inputs
			await convertWitnessUtxoToNonWitnessUtxo(input, apiClient);
		}
	}
}; 